import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Select,
  Radio,
  Button,
  Tag,
  Alert,
  message,
  Spin,
  Empty,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PictureOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import customClientAxios from "../customAxios";
import {
  AdminNurtureTemplates,
  AdminNurtureTemplateImages,
  AdminNurtureTemplateImageMode,
  AdminNurtureTemplateImageUpload,
} from "../utils/ApiEndpoints";

// ── Sequence catalog (mirror Nurture/NurtureTemplates.js) ──────────────────
const SEQUENCES = [
  { key: "prospect",            label: "Prospect" },
  { key: "qualified",           label: "Qualified" },
  { key: "onboarded",           label: "Onboarded" },
  { key: "brand_page",          label: "Brand Page" },
  { key: "event_created",       label: "Event Created" },
  { key: "first_sale",          label: "First Sale" },
  { key: "purchased_retention", label: "Plan Purchased" },
];

const MODE_DESCRIPTIONS = {
  none: "No header image — message goes out as text only.",
  single: "Same image for every recipient. Uploaded image acts both as the Meta approval sample and the live header.",
  per_category: "Personalize the image by the contact's Main Category. The 'Default' image is the Meta approval sample and the fallback when a contact's category has no image set.",
};

// ── Styled ────────────────────────────────────────────────────────────────
const Page = styled.div`
  padding: 24px 32px 80px;
  background: #f8fafc;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 20px;
  h1 { margin: 0 0 4px; font-size: 24px; font-weight: 700; color: #0f172a; }
  p { margin: 0; color: #64748b; font-size: 13px; }
`;

const SelectorBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;

  .label { font-size: 12px; color: #64748b; }
`;

const ModeCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;

  .row { display: flex; gap: 16px; align-items: flex-start; }
  .desc { font-size: 12px; color: #64748b; margin-top: 8px; max-width: 720px; }
  .badge { font-size: 11px; color: #94a3b8; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${(p) => (p.$highlight ? "#f59e0b" : "#e2e8f0")};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease;
  ${(p) => p.$highlight && "box-shadow: 0 0 0 2px rgba(245,158,11,0.15);"}
  &:hover { box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06); }
`;

const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 1.91 / 1;
  background: #f1f5f9 url(${(p) => p.$url || ""}) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 28px;
`;

const CardBody = styled.div`
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  .title-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .name { font-size: 13px; font-weight: 600; color: #0f172a; }
  .leadcount { font-size: 11px; color: #64748b; }
`;

// ── Component ─────────────────────────────────────────────────────────────
export default function NurtureCategoryImages() {
  const [sequenceKey, setSequenceKey] = useState("prospect");
  const [stepDay, setStepDay] = useState(1);
  const [stepOptions, setStepOptions] = useState([]); // for the active sequence
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const fileRefs = useRef({}); // ref per bucket key

  // ── Fetch steps for the active sequence (mode selector population) ──────
  const fetchSteps = useCallback(async () => {
    try {
      const r = await customClientAxios().get(AdminNurtureTemplates, {
        params: { sequenceKey },
      });
      const rows = r.data?.data || [];
      const opts = rows.map((row) => ({
        value: row.stepDay,
        label: `Day ${row.stepDay} — ${row.template?.displayName || row.template?.name || "(no template)"}`,
      }));
      setStepOptions(opts);
      // Reset to the first step when sequence changes
      if (opts.length && !opts.find((o) => o.value === stepDay)) {
        setStepDay(opts[0].value);
      }
    } catch (e) {
      console.error(e);
    }
  }, [sequenceKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch the images + mode for the selected (sequence, step) ───────────
  const fetchImages = useCallback(async () => {
    if (!stepDay) return;
    setLoading(true);
    setError(null);
    try {
      const r = await customClientAxios().get(
        AdminNurtureTemplateImages(stepDay, sequenceKey)
      );
      setData(r.data?.data || null);
    } catch (e) {
      setError(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Failed to load images"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sequenceKey, stepDay]);

  useEffect(() => {
    fetchSteps();
  }, [fetchSteps]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // ── Mode toggle ─────────────────────────────────────────────────────────
  const changeMode = async (mode) => {
    setBusyKey("__mode__");
    try {
      await customClientAxios().patch(
        AdminNurtureTemplateImageMode(stepDay, sequenceKey),
        { mode }
      );
      message.success(`Mode set to ${mode.replace("_", " ")}`);
      await fetchImages();
    } catch (e) {
      message.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Failed to switch mode"
      );
    } finally {
      setBusyKey(null);
    }
  };

  // ── Upload + remove handlers ────────────────────────────────────────────
  const onPickFile = (bucketKey) => {
    const ref = fileRefs.current[bucketKey];
    if (ref) {
      ref.value = "";
      ref.click();
    }
  };

  const uploadFor = async (bucketKey, file) => {
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png"];
    if (!okTypes.includes(file.type)) {
      message.error("WhatsApp template headers only accept JPG or PNG (not WEBP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Image must be ≤ 5MB.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    setBusyKey(bucketKey);
    try {
      await customClientAxios().post(
        AdminNurtureTemplateImageUpload(stepDay, bucketKey, sequenceKey),
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      message.success(
        bucketKey === "default"
          ? "Default image uploaded — also synced to msg91 as the Meta approval sample."
          : "Image uploaded."
      );
      await fetchImages();
    } catch (e) {
      message.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Upload failed"
      );
    } finally {
      setBusyKey(null);
    }
  };

  const removeFor = async (bucketKey) => {
    setBusyKey(bucketKey);
    try {
      await customClientAxios().delete(
        AdminNurtureTemplateImageUpload(stepDay, bucketKey, sequenceKey)
      );
      message.success("Removed.");
      await fetchImages();
    } catch (e) {
      message.error(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          "Remove failed"
      );
    } finally {
      setBusyKey(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const mode = data?.imageMode || "none";
  const buckets = data?.buckets || [];
  // In single mode we hide the grid — single uses the existing template
  // editor's headerMedia path. Surface a hint instead.
  const showGrid = mode === "per_category";

  return (
    <Page>
      <Header>
        <h1>Category Images</h1>
        <p>
          Customize the WhatsApp header image per Main Category, or use a single
          image for everyone. Mode is configured per sequence step.
        </p>
      </Header>

      <SelectorBar>
        <div className="label">Sequence</div>
        <Select
          value={sequenceKey}
          onChange={(v) => setSequenceKey(v)}
          options={SEQUENCES.map((s) => ({ value: s.key, label: s.label }))}
          style={{ minWidth: 180 }}
        />
        <div className="label" style={{ marginLeft: 12 }}>Step</div>
        <Select
          value={stepDay}
          onChange={(v) => setStepDay(v)}
          options={stepOptions}
          style={{ minWidth: 320 }}
          loading={!stepOptions.length}
        />
      </SelectorBar>

      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}

      <Spin spinning={loading}>
        <ModeCard>
          <div className="row">
            <div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                Image mode
              </div>
              <Radio.Group
                value={mode}
                onChange={(e) => changeMode(e.target.value)}
                disabled={busyKey === "__mode__"}
                optionType="button"
                buttonStyle="solid"
                options={[
                  { value: "none", label: "None (text-only)" },
                  { value: "single", label: "Same for everyone" },
                  { value: "per_category", label: "Per Main Category" },
                ]}
              />
              <div className="desc">{MODE_DESCRIPTIONS[mode]}</div>
              {data?.approvalSample && (
                <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>
                  Approval sample on template:{" "}
                  <a href={data.approvalSample.url} target="_blank" rel="noreferrer">
                    {data.approvalSample.url.split("/").slice(-1)[0]}
                  </a>{" "}
                  · template status:{" "}
                  <Tag color={data.approvalSample.templateStatus === "approved" ? "success" : "default"}>
                    {data.approvalSample.templateStatus}
                  </Tag>
                </div>
              )}
            </div>
          </div>
        </ModeCard>

        {mode === "single" && (
          <Alert
            type="info"
            showIcon
            message="Single-image mode uses the image attached to the template via the Sequence Manager edit drawer."
            description="To upload, edit, or replace it, go to WA Templates → click Edit on this step → Header image section. Switch to 'Per Main Category' here if you want to vary the image by contact category."
            style={{ marginBottom: 16 }}
          />
        )}

        {mode === "none" && (
          <Alert
            type="info"
            showIcon
            message="No header image — switch to 'Same for everyone' or 'Per Main Category' to add images."
            description="Going from 'none' to any image mode requires re-submitting the template to Meta for fresh approval (the template structure changes)."
            style={{ marginBottom: 16 }}
          />
        )}

        {showGrid && (
          <>
            <div style={{ fontSize: 13, color: "#64748b", margin: "8px 0 12px" }}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              The <strong>Default</strong> image is required — it's used as Meta's approval sample
              and as the fallback for contacts whose Main Category has no image uploaded yet.
            </div>
            <Grid>
              {buckets.map((b) => (
                <Card key={b.key} $highlight={b.key === "default"}>
                  <Thumb $url={b.image?.url}>
                    {!b.image?.url && <PictureOutlined />}
                  </Thumb>
                  <CardBody>
                    <div className="title-row">
                      <span className="name">{b.label}</span>
                      <Tooltip title="Number of contacts mapped to this bucket across all stages">
                        <span className="leadcount">{b.leadCount} contacts</span>
                      </Tooltip>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: "auto" }}>
                      <input
                        ref={(el) => (fileRefs.current[b.key] = el)}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadFor(b.key, f);
                        }}
                      />
                      <Button
                        size="small"
                        icon={<UploadOutlined />}
                        onClick={() => onPickFile(b.key)}
                        loading={busyKey === b.key}
                        type={b.image?.url ? "default" : "primary"}
                      >
                        {b.image?.url ? "Replace" : "Upload"}
                      </Button>
                      {b.image?.url && (
                        <Popconfirm
                          title="Remove this image?"
                          description={
                            b.key === "default"
                              ? "Default is required while in 'per_category' mode. Switch the mode first if you want to remove it."
                              : "Contacts in this bucket will fall back to the Default image."
                          }
                          onConfirm={() => removeFor(b.key)}
                          okText="Remove"
                          okButtonProps={{ danger: true }}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={busyKey === b.key}
                          />
                        </Popconfirm>
                      )}
                    </div>
                  </CardBody>
                </Card>
              ))}
            </Grid>
          </>
        )}

        {!loading && !data && !error && (
          <Empty description="Pick a sequence and step to manage its images." />
        )}
      </Spin>
    </Page>
  );
}
